<template>
  <div>
    <AnnouncementFilter
      :csv-filter-options="displayedFilterOptions"
      :already-selected-displayed-columns="filtersData?.displayedColumns"
      @apply="applyFilters"
    />
    <div v-if="isLoading" class="mt-28">
      <Loader :content="true" />
    </div>
    <div v-else-if="!isLoading && isEmpty(notificationList)">
      <NoRecordFound />
    </div>
    <TableWrapper v-else>
      <THead>
        <TRHead>
          <TH
            v-for="head in tableHeaders.filter((head) => !head.skipForDataTableHead)"
            v-show="
              showColumnInDataTable(filtersData, head.dataTableName, [
                'Title',
                'Role',
                'Medium',
                'Sent By',
                'Date',
              ])
            "
            :key="head.id"
          >
            {{ head.dataTableName }}
          </TH>
        </TRHead>
      </THead>
      <TBody>
        <TRBody v-for="(notification, index) in notificationList" :key="index">
          <TD v-show="showColumnInDataTable(filtersData, 'Title', ['Title'])">
            <span
              class="text-primary-purple-600 cursor-pointer"
              @click="redirectToNotificationDetails(notification)"
            >
              {{ notification.title }}
            </span>
          </TD>

          <TD v-show="showColumnInDataTable(filtersData, 'Type')">
            {{ notification?.announcement_type?.title || GENERAL_CONSTANTS.NOT_APPLICABLE }}
          </TD>
          <TD v-show="showColumnInDataTable(filtersData, 'Role', ['Role'])">
            {{ notification.role }}
          </TD>
          <TD v-show="showColumnInDataTable(filtersData, 'Medium', ['Medium'])">
            {{ notification.medium }}
            <span
              v-for="(val, ind) in notification.mediums"
              :key="ind"
              class="text-text-color text-sm"
            >
              {{ val }}
              <span v-if="ind + 1 < notification.mediums.length">,</span>
            </span>
          </TD>

          <TD v-show="showColumnInDataTable(filtersData, 'Role Context')">
            <span class="text-text-color text-sm">
              <div class="rounded-xl p-3 relative">
                <RoleContext
                  :notification="notification"
                  :dropdown-top-bottom="
                    index === notificationList.length - 1 ? 'bottom-10' : 'top-10'
                  "
                  :tooltip="
                    index === notificationList.length - 1 ? 'tooltip-bottom' : 'tooltip-top'
                  "
                />
              </div>
            </span>
          </TD>

          <TD v-show="showColumnInDataTable(filtersData, 'Total Receivers')">
            <span
              class="cursor-pointer text-primary-purple-600"
              @click="redirectToNotificationDetails(notification)"
            >
              {{ notification.stats.receivers_count }}
            </span>
          </TD>

          <TD v-show="showColumnInDataTable(filtersData, 'Sent By', ['Sent By'])">
            <span>
              <SingleUserDisplay
                :user="notification.inserted_by"
                :image="true"
                label="full_name"
                :tooltip="index === notificationList.length - 1 ? 'tooltip-bottom' : 'tooltip-top'"
              />
            </span>
          </TD>

          <TD v-show="showColumnInDataTable(filtersData, 'Date', ['Date'])">
            {{ $filters.dateFormatInUTC(notification?.inserted_at, TIME_CONSTANTS.DATE) }}
          </TD>
          <TD v-show="showColumnInDataTable(filtersData, 'Status')">
            <UiBadgeDisplay
              :options="notification.mediumStatus"
              label="title"
              :dropdown-top-bottom="dropdownTopBottom(index, notificationList, 'bottom-6', 'top-6')"
              :tooltip="dropdownTopBottom(index, notificationList, 'tooltip-bottom', 'tooltip-top')"
            />
          </TD>
          <TD v-show="showColumnInDataTable(filtersData, 'Actions')">
            <TableAction
              :current-user="notification"
              :idx="index"
              :action-list="handlerActionList(notification.status)"
              @action="typeAction"
            />
          </TD>
        </TRBody>
      </TBody>
    </TableWrapper>
    <Pagination
      v-if="totalNotificationCount > GENERAL_CONSTANTS.RECORD_LIMIT"
      v-show="!isLoading"
      :key="`ForceRender${forceRender}`"
      :record-limit="filteredRecordLimit"
      :max-range="paginationCounts(totalNotificationCount, filteredRecordLimit)"
      @filterRecord="_getAnnouncements"
    />
    <SingleNotificationModal v-if="showModal" @updateResult="getNewResults" />
    <UIConfirmationModal
      v-if="confirmation"
      heading="RETRY_REQ"
      message="Are you sure you want to retry "
      button-text="Retry"
      :modal="confirmation"
      :name="currentAnnouncement.title"
      remaining-message="Request ?"
      @cancel="toggleConfirmationModal"
      @confirm="retryTheAnnouncementRequest"
    />
    <UIConfirmationModal
      v-if="resendModalConfirmation"
      :modal="resendModalConfirmation"
      heading="RESEND_REQ"
      message="Are you sure you want to resend "
      button-text="Resend"
      :name="currentAnnouncement.title"
      remaining-message="Request ?"
      @cancel="toggleResendConfirmationModal"
      @confirm="resendTheAnnouncementRequest"
    />
  </div>
</template>

<script>
import { dropdownTopBottom } from '@src/utils/generalUtil.js'
import isEmpty from 'lodash/isEmpty'
import RoleContext from '@views/notifications/RoleContext.vue'
import Loader from '@components/BaseComponent/Loader.vue'
import TableAction from '@/src/components/TableAction.vue'
import { mapActions, mapState } from 'vuex'
import generalUtil from '@src/mixins/general-mixins.js'
import SingleNotificationModal from '@/src/router/views/admin/modals/AnnouncementUpsertModal.vue'
import Pagination from '@components/BaseComponent/Pagination.vue'
import AnnouncementFilter from '@src/router/views/notifications/AnnouncementFilter.vue'
import {
  transformTextToCapitalize,
  objectDeepCopy,
  showColumnInDataTable,
} from '@utils/generalUtil'
import FILTER_KEYS from '@src/constants/filter-constants.js'
import fileMixins from '@/src/mixins/file-mixins'
import { getTimeFromDateWithUTC } from '@utils/moment.util'
import TIME_CONSTANTS from '@src/constants/date-time-constants'
import GENERAL_CONSTANTS from '@src/constants/general-constants'
import scrollMixin from '@src/mixins/scroll-mixin'
import SingleUserDisplay from '@/src/components/UiElements/SingleUserDisplay.vue'
import NoRecordFound from '@/src/components/BaseComponent/NoRecordFound.vue'
import { fullName } from '@/src/utils/settings/tenant-user.util'
import UIConfirmationModal from '@src/components/UiElements/UIConfirmationModal.vue'
import UiBadgeDisplay from '@src/components/UiElements/UiBadgeDisplay.vue'
import {
  AnnouncementGraphChart,
  rightBarStats,
} from '@/src/router/views/notifications/Announcement.util.js'
import { buildWhereQuery } from '@src/utils/filters/index.js'
import AnnouncementData from '@/src/router/views/notifications/Announcement.json'
import {
  TableWrapper,
  TBody,
  TD,
  TH,
  THead,
  TRBody,
  TRHead,
} from '@src/components/UiElements/TableElements/index.js'

export default {
  components: {
    RoleContext,
    Loader,
    AnnouncementFilter,
    TableAction,
    SingleNotificationModal,
    Pagination,
    SingleUserDisplay,
    NoRecordFound,
    UIConfirmationModal,
    UiBadgeDisplay,
    TableWrapper,
    TBody,
    TD,
    TH,
    THead,
    TRBody,
    TRHead,
  },
  mixins: [generalUtil, fileMixins, scrollMixin],
  data() {
    return {
      TIME_CONSTANTS,
      GENERAL_CONSTANTS,
      isEmpty,
      result: 'result',
      title: 'title',
      dashboard: 'dashboard',
      options: ['Select'],
      defaultSelectedColumns: AnnouncementData.defaultSelectedColumns,
      tableHeaders: AnnouncementData.tableHeaders,
      forceRender: 0,
      totalNotificationCount: 0,
      notificationList: [],
      filteredRecordLimit: 10,
      isLoading: false,
      isHideNotify: false,
      filtersData: {},
      noRecord: false,
      actionList: [],
      confirmation: false,
      currentAnnouncement: null,
      resendModalConfirmation: false,
      announcementRetrySucessMessage: 'Job started for resending failed announcements',
    }
  },
  page: {
    title: 'Notification | Single',
    meta: [
      {
        name: 'description',
        content: 'Single Notifications',
      },
    ],
  },
  computed: {
    ...mapState({
      showModal: (state) => state.layout.showModal,
      currentCampusScope: (state) => state.layout.currentCampusScope,
      tabRightBar: (state) => state.layout.tabRightNav,
    }),
  },
  watch: {
    tabRightBar: {
      immediate: true,
      handler(value) {
        if (value) this.setRightbar()
      },
    },
    currentCampusScope: {
      handler(val) {
        if (val) {
          this.getNewResults()
          this.setRightbar()
        }
      },
      deep: true,
    },
  },
  created() {
    this.setRightbar()
    this._getAnnouncements()
  },
  methods: {
    dropdownTopBottom,
    showColumnInDataTable,
    async getNewResults() {
      await this._getAnnouncements()
      this.forceRender++
    },
    applyFilters(filtersPayload) {
      this.filtersData = objectDeepCopy(filtersPayload)
      this.getNewResults()
    },
    redirectToNotificationDetails(notification) {
      this.$router?.push({
        name: 'announcement-detail',
        params: {
          announcementId: notification.id,
        },
      })
      this.isHideNotify = true
      const statsPayload = {
        $where: {
          ...buildWhereQuery(FILTER_KEYS.EQUAL, 'id', notification.id),
        },
      }
      this.getAnnouncementStats(statsPayload).then((res) => {
        this.setRightbar(res?.data)
      })
    },

    toggleConfirmationModal() {
      this.confirmation = !this.confirmation
      if (!this.confirmation) this.currentAnnouncement = null
    },
    toggleResendConfirmationModal() {
      this.resendModalConfirmation = !this.resendModalConfirmation
      if (!this.resendModalConfirmation) this.currentAnnouncement = null
    },

    typeAction(action, currentAnnouncement) {
      this.currentAnnouncement = currentAnnouncement
      switch (action) {
        case 'SNA_RET':
          this.toggleConfirmationModal()
          break
        case 'SNA_RES':
          this.toggleResendConfirmationModal()
          break
      }
    },

    async retryTheAnnouncementRequest() {
      const res = await this.retryAnnouncementRequest(this.currentAnnouncement.id)
      this.confirmation = false
      this.currentAnnouncement = null
      this.$store.commit('toast/NEW', {
        message: res?.data?.message || this.announcementRetrySucessMessage,
        type: 'success',
      })
    },

    async resendTheAnnouncementRequest() {
      const res = await this.resendAnnouncementRequest(this.currentAnnouncement.id)
      this.resendModalConfirmation = false
      this.currentAnnouncement = null
      await this._getAnnouncements()
      this.$store.commit('toast/NEW', {
        message: res?.data?.message || this.announcementRetrySucessMessage,
        type: 'success',
      })
    },

    async _getAnnouncements(range) {
      if (!this.currentCampusScope) return
      this.isLoading = true
      const paginationLimit = {
        skip: range?.skip || GENERAL_CONSTANTS.RECORD_SKIP,
        limit: range?.limit || GENERAL_CONSTANTS.RECORD_LIMIT,
      }

      const payload = {
        ...paginationLimit,
        ...(this.filtersData?.$where ? { $where: this.filtersData.$where } : {}),
      }

      const [res, err] = await this.getAnnouncemnt(payload)

      if (err) {
        this.isLoading = false
        console.error('Error fetching announcements:', err)
        return
      }

      this.totalNotificationCount = res?.data?.meta?.total_records || 0

      const statsRes = await this.getAnnouncementStats()
      if (statsRes?.data) {
        this.setRightbar(statsRes.data)
      }

      this.notificationList = res?.data?.records?.map((notification) => {
        notification.mediums = this.extractMediums(notification.stats)
        notification.role = transformTextToCapitalize(notification.receiver_role_id)

        if (notification.inserted_by) {
          notification.inserted_by.full_name = `${notification.inserted_by.first_name} ${notification.inserted_by.last_name}`
        }

        if (notification.announcement_receivers?.length) {
          notification.announcement_receivers.forEach((receiver) => fullName(receiver))
        }

        notification.mediumStatus = this.extractMediumStatus(notification.stats)

        return notification
      })

      this.isLoading = false
    },
    handlerActionList(status) {
      if (status === 'sent') {
        return (this.actionList = [{ name: 'SNA_RES' }])
      } else {
        return (this.actionList = [{ name: 'SNA_RET' }, { name: 'SNA_RES' }])
      }
    },

    setRightbar(stats = []) {
      const BAR_GRAPH_VALUE = [
        stats?.total_announcements,
        stats?.staff_announcements,
        stats?.teacher_announcements,
        stats?.student_announcements,
        stats?.guardian_announcements,
      ]
      const PIE_GRAPH_VALUE = [stats?.total_failed, stats?.total_sent]
      const rightBarContent = {
        header: {
          title: 'Notifications',
        },
        stats: rightBarStats(),
        chartData: AnnouncementGraphChart(BAR_GRAPH_VALUE, PIE_GRAPH_VALUE),
      }
      if (!this.isHideNotify) {
        rightBarContent.header.buttons = [
          {
            title: 'NOTIFY',
            classes: ['primary-button-right'],
            action: { name: 'layout/setShowModal' },
          },
        ]
      }
      this.setRightbarContent(rightBarContent)
    },
    extractMediums(stats) {
      const mediums = []
      if (stats.total_email_count) mediums.push('email')
      if (stats.total_webpush_count) mediums.push('push notification')
      if (stats.total_sms_count) mediums.push('sms')
      return mediums
    },
    extractMediumStatus(stats) {
      const mediumStatus = []
      if (stats.total_sms_count) {
        mediumStatus.push({
          title: `SMS: ${stats.sms_sent_count} / ${stats.total_sms_count}`,
        })
      }
      if (stats.total_email_count) {
        mediumStatus.push({
          title: `Email: ${stats.email_sent_count} / ${stats.total_email_count}`,
        })
      }
      if (stats.total_webpush_count) {
        mediumStatus.push({
          title: `Webpush: ${stats.webpush_sent_count} / ${stats.total_webpush_count}`,
        })
      }
      return mediumStatus
    },

    ...mapActions('notifications', [
      'getAnnouncemnt',
      'retryAnnouncementRequest',
      'getAnnouncementStats',
      'resendAnnouncementRequest',
    ]),
    ...mapActions('layout', ['setRightbarContent', 'setRightBarStats', 'setShowModal']),
  },
}
</script>
